import { useUpdateMerchantTemplate } from "@/services/merchant/utilities";
import SettingsShell, { SettingsSection } from "@/modules/settings/SettingsShell";
import settings from "@/modules/settings/settings.module.css";
import useUpdateMerchantBusiness from "../../hooks/useUpdateMerchantBusiness";
import { DEFAULT_THEME } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { Button, Card, Group, Radio, SimpleGrid, Skeleton, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useGetMerchant from "../../hooks/useGetMerchant";
import classes from "./siteTemplate.module.css";
import { merchantSiteUrl } from "@/utilities/helper/merchantSite";

const templates = [
  { value: "serene", title: "آرام / Serene", theme: "serene", image: "/site-templates/serene.jpg" },
  { value: "luxury", title: "Luxury", theme: "luxury", image: "/site-templates/luxury-rose.png" },
  { value: "default", title: "پیش‌فرض", theme: "default" },
] as const;

const SWATCHES = ["red", "pink", "violet", "indigo", "blue", "cyan", "green", "lime", "yellow", "orange", "gray"] as const;

// «قالب و رنگ سایت»: the booking site's template and its brand colour, saved together.
function SiteTemplate() {
  const queryClient = useQueryClient();
  const { data: merchant, isLoading } = useGetMerchant();
  const { mutate, isLoading: isSaving } = useUpdateMerchantTemplate();
  const [selected, setSelected] = useState("default");
  const [color, setColor] = useState<string | undefined>();
  const { updateMerchant, isLoading: savingColor } = useUpdateMerchantBusiness();
  const siteUrl = merchantSiteUrl(merchant?.businessInfo?.subDomain);

  useEffect(() => {
    if (merchant) setColor(merchant.businessInfo?.theme ?? undefined);
    if (merchant) setSelected(templates.some((template) => template.value === merchant.template) ? merchant.template! : "default");
  }, [merchant]);

  const savedTemplate = templates.some((t) => t.value === merchant?.template) ? merchant?.template : "default";
  const dirty = !!merchant && (selected !== savedTemplate || (color ?? "") !== (merchant.businessInfo?.theme ?? ""));
  const reset = () => { setSelected(savedTemplate ?? "default"); setColor(merchant?.businessInfo?.theme ?? undefined); };
  const save = () => {
    if (color !== (merchant?.businessInfo?.theme ?? undefined)) {
      const info = merchant?.businessInfo;
      // Only the colour changes; the other business fields go back as they are.
      updateMerchant({ title: info?.title ?? "", merchantTypeId: info?.merchantType?.id?.toString(), subDomain: info?.subDomain || undefined, voiceType: info?.voiceType, theme: color }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["getMerchant"] });
          notifications.show({ message: "رنگ سایت ذخیره شد", color: "green" });
        },
      });
    }
    if (selected === savedTemplate) return;
    mutate(
      { template: selected },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["getMerchant"] });
          notifications.show({
            title: "عملیات موفق",
            message: "قالب سایت مجموعه با موفقیت تغییر کرد.",
            color: "green",
          });
        },
      },
    );
  };

  return (
    <SettingsShell title="قالب و رنگ سایت" description="ظاهر سایت رزرو: قالب و رنگ اصلی دکمه‌ها و نوارها." onSubmit={(e) => { e.preventDefault(); save(); }} dirty={dirty} saving={isSaving || savingColor} onReset={reset}>
      <SettingsSection title="رنگ سایت" description="رنگ دکمه‌ها و نوارهای سایت رزرو. قالب «آرام» هم رنگ خود را از همین می‌گیرد.">
        <div className={settings.swatches} role="radiogroup" aria-label="رنگ سایت">
          {SWATCHES.map((name) => (
            <button key={name} type="button" role="radio" aria-checked={color === name} aria-label={name} className={settings.swatch} style={{ background: DEFAULT_THEME.colors[name][5] }} onClick={() => setColor(name)}>
              {color === name && <IconCheck size={16} color="#fff" stroke={3} />}
            </button>
          ))}
        </div>
      </SettingsSection>
      <SettingsSection title="قالب" description="نمای کلی سایت؛ با «پیش‌نمایش» قبل از انتشار ببینید.">
        <Stack gap="lg">

          {isLoading ? (
            <SimpleGrid cols={{ base: 1, sm: 2 }}><Skeleton h={260} /><Skeleton h={260} /></SimpleGrid>
          ) : (
            <Radio.Group value={selected} onChange={setSelected}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {templates.map((template) => (
                  <Card component="label" key={template.value} withBorder padding="sm"
                    className={classes.card} data-selected={selected === template.value || undefined}>
                    {"image" in template ? (
                      <div className={`${classes.preview} ${classes.screenshot}`}>
                        <img src={template.image} alt={`نمای قالب ${template.title}`} loading="lazy" />
                      </div>
                    ) : (
                      <div className={`${classes.preview} ${classes[template.theme]}`}>
                        <div className={classes.previewHeader}><span /><span /><span /></div>
                        <div className={classes.previewHero}><i /><div><b /><b /><em /></div></div>
                        <div className={classes.previewGrid}><span /><span /><span /></div>
                      </div>
                    )}
                    <Group justify="space-between" mt="sm">
                      <div><Text fw={700}>{template.title}</Text><Text size="xs" c="dimmed">{template.value}</Text></div>
                      <Radio value={template.value} aria-label={`انتخاب قالب ${template.title}`} />
                    </Group>
                  </Card>
                ))}
              </SimpleGrid>
            </Radio.Group>
          )}

          {selected === "serene" && merchant?.businessInfo?.subDomain && <Stack gap="xs">
            <Button component="a" href={`${siteUrl}/?previewTemplate=serene`} target="_blank" rel="noopener noreferrer" variant="outline">پیش‌نمایش آرام با اطلاعات مجموعه</Button>
            <Text size="xs" c="dimmed">پیش‌نمایش قالب فعال را تغییر نمی‌دهد؛ برای انتشار «ذخیره» را بزنید.</Text>
          </Stack>}
        </Stack>
      </SettingsSection>
    </SettingsShell>
  );
}

export default SiteTemplate;
